import { Grid } from '@mui/material'
import { fromRelationshipCode, getDateFromString } from '../utils'
import { DataPanel } from './DataPanel'
import { DataTable } from './DataTable'

interface ClaimSummaryProps {
    claimNumber: string
    data: any
}

export const ClaimSummary = (props: ClaimSummaryProps) => {
    const { claimNumber, data } = props
    // TODO: refactor w/ state + useEff
    let isClaimPrivateDataAvailable = false
    let patientName: string | undefined
    let physicianName: string | undefined
    let patientDateOfBirth: string | undefined

    let status = 'Submitted'

    if (data.claim !== undefined) {
        const privateData = data.claim?.privateData
        if (privateData !== undefined) {
            isClaimPrivateDataAvailable = true
            patientName = `${privateData.patientFirstName} ${privateData.patientLastName}`
            physicianName = `${privateData.individualPhysicianFirstName} ${privateData.individualPhysicianLastName}`
            const patientDateOfBirthString = data.claim.privateData.patientDateOfBirth
            if (patientDateOfBirthString !== undefined) {
                patientDateOfBirth =
                    getDateFromString(patientDateOfBirthString).toLocaleDateString()
            }
            switch (data.claim.privateData.frequencyCode) {
                case '5':
                    status = 'Late charges only'
                    break
                case '7':
                    status = 'Replacement of prior claim'
                    break
                case '8':
                    status = 'Void / cancel prior claim'
                    break
            }
        }
    } else {
        isClaimPrivateDataAvailable = true
    }

    let totalPaid = '--'
    let paymentDate = '--'
    const patientCostShare = '--'
    if (data.payment !== undefined && data.payment !== null) {
        for (const payment of data.payment.publicData.payments) {
            if (payment.patientControlNumber === claimNumber) {
                status = 'Paid'
                totalPaid =
                    payment.amountPaidByPayer > 0
                        ? `$${payment.amountPaidByPayer} to Provider`
                        : '--'
                paymentDate = getDateFromString(
                    data.payment.publicData.paymentDate,
                ).toLocaleDateString()
            }
        }
    }

    return (
        <Grid container spacing={4}>
            <Grid item lg={6} md={12} xs={12}>
                <DataPanel
                    minHeight={150}
                    title="Payer Group"
                    content={
                        <DataTable
                            isPrivateDataAvailable={isClaimPrivateDataAvailable}
                            data={[
                                {
                                    key: 'Payer Name',
                                    value: data.claim?.publicData.payerName,
                                    private: false,
                                },
                                {
                                    key: 'Payer ID Number',
                                    value: data.claim?.publicData.payerIdNumber,
                                    private: false,
                                },
                                {
                                    key: 'EDI Number',
                                    value: data.claim?.privateData?.payerEDINumber,
                                    private: true,
                                },
                            ]}
                        />
                    }
                />
            </Grid>
            <Grid item lg={6} md={12} xs={12}>
                <DataPanel
                    minHeight={150}
                    title="Subscriber"
                    content={
                        <DataTable
                            isPrivateDataAvailable={isClaimPrivateDataAvailable}
                            data={[
                                {
                                    key: 'Member ID',
                                    value: data.claim?.privateData?.payerSubscriberMemberId,
                                    private: true,
                                },
                                {
                                    key: 'Medicare Insurance ID',
                                    value: data.claim?.privateData?.medicareInsuranceId,
                                    private: true,
                                },
                            ]}
                        />
                    }
                />
            </Grid>
            <Grid item lg={6} md={12} xs={12}>
                <DataPanel
                    minHeight={250}
                    title="Provider Group"
                    content={
                        <DataTable
                            isPrivateDataAvailable={isClaimPrivateDataAvailable}
                            data={[
                                {
                                    key: 'Provider Group Name',
                                    value: data.claim?.publicData.providerGroupName,
                                    private: false,
                                },
                                {
                                    key: 'Provider TAX ID',
                                    value: data.claim?.privateData?.providerTaxIDNumber,
                                    private: true,
                                },
                                {
                                    key: 'Provider Group NPI',
                                    value: data.claim?.publicData.providerGroupNPI,
                                    private: false,
                                },
                                {
                                    key: 'EDI Number',
                                    value: data.claim?.privateData?.providerGroupEDINumber,
                                    private: true,
                                },
                            ]}
                        />
                    }
                />
            </Grid>
            <Grid item lg={6} md={12} xs={12}>
                <DataPanel
                    minHeight={250}
                    title="Patient"
                    content={
                        <DataTable
                            isPrivateDataAvailable={isClaimPrivateDataAvailable}
                            data={[
                                { key: 'Patient Name', value: patientName, private: true },
                                {
                                    key: 'Patient Date of Birth',
                                    value: patientDateOfBirth,
                                    private: true,
                                },
                                {
                                    key: 'Patient sex',
                                    value: data.claim?.privateData?.patientSex,
                                    private: true,
                                },
                                {
                                    key: 'Subscriber relationship',
                                    value: fromRelationshipCode(
                                        data.claim?.privateData?.relationshipToSubscriberCode,
                                    ),
                                    private: true,
                                },
                                {
                                    key: 'Patient Address1',
                                    value: data.claim?.privateData?.patientAddressLine1,
                                    private: true,
                                },
                                {
                                    key: 'City',
                                    value: data.claim?.privateData?.patientAddressCity,
                                    private: true,
                                },
                                {
                                    key: 'State',
                                    value: data.claim?.privateData?.patientAddressState,
                                    private: true,
                                },
                                {
                                    key: 'Zip',
                                    value: data.claim?.privateData?.patientAddressPostalCode,
                                    private: true,
                                },
                            ]}
                        />
                    }
                />
            </Grid>
            <Grid item lg={6} md={12} xs={12}>
                <DataPanel
                    minHeight={200}
                    title="Individual Physician"
                    content={
                        <DataTable
                            isPrivateDataAvailable={isClaimPrivateDataAvailable}
                            data={[
                                { key: 'Physician Name', value: physicianName, private: true },
                                {
                                    key: 'Physician NPI',
                                    value: data.claim?.publicData.individualPhysicianNPI,
                                    private: false,
                                },
                                {
                                    key: 'Service Address1',
                                    value: data.claim?.privateData?.serviceAddressLine1,
                                    private: true,
                                },
                                {
                                    key: 'City',
                                    value: data.claim?.privateData?.serviceAddressCity,
                                    private: true,
                                },
                                {
                                    key: 'State',
                                    value: data.claim?.privateData?.serviceAddressState,
                                    private: true,
                                },
                                {
                                    key: 'Zip',
                                    value: data.claim?.privateData?.serviceAddressPostalCode,
                                    private: true,
                                },
                            ]}
                        />
                    }
                />
            </Grid>
            <Grid item lg={6} md={12} xs={12}>
                <DataPanel
                    minHeight={200}
                    title="Summary"
                    content={
                        <DataTable
                            isPrivateDataAvailable={true}
                            data={[
                                { key: 'Status', value: status, private: false },
                                { key: 'Date of Payment', value: paymentDate, private: false },
                                { key: 'Total Paid (USD)', value: totalPaid, private: false },
                                {
                                    key: 'Patient Cost Share',
                                    value: `${patientCostShare}`,
                                    private: false,
                                },
                            ]}
                        />
                    }
                />
            </Grid>
        </Grid>
    )
}
